import { MutateOptions } from '@tanstack/react-query';
import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { HttpException } from '../../../../exceptions/index';
import { AnalyticHooks, Stakeholder, useLocale, useModalState } from '../../../../hooks/index';
import { PossibleAction } from '../../../../types/google-analytics-types.types';
import { CompanyStakeholderDto, HumanStakeholderDto } from '../../../../types/stakeholder.types';
import UpdateStakeholderModal from '../../modals/update-stakeholder-modal';
import { EditStakeholderVariant } from '../stakeholder-actions.types';
import EditStakeholderForm from './edit-stakeholder-form';

export type EditStakeholderModalProps = {
  companyId: string;
  invalidateQuery: () => void;
  isDefaultEditing: boolean;
  isOpenModal: boolean;
  onEditClick: (props: { id: string; step: EditStakeholderVariant }) => void;
  setClose: () => void;
  stakeholderId: string;
  step: EditStakeholderVariant;
};

const EditStakeholderModal: FC<EditStakeholderModalProps> = ({
  isOpenModal,
  setClose,
  companyId,
  stakeholderId,
  invalidateQuery,
  isDefaultEditing,
  onEditClick,
  step,
}) => {
  const { stakeholder, isLoading } = Stakeholder.useStakeholder({ companyId, stakeholderId });

  const editStakeholderTracker = AnalyticHooks.useClick(PossibleAction.EDIT_STAKEHOLDER);
  const { update } = Stakeholder.useUpdate();
  const { messagesLocale } = useLocale();

  const [context, setContext] = useState<{
    options:
      | MutateOptions<unknown, HttpException, CompanyStakeholderDto | HumanStakeholderDto, unknown>
      | undefined;
    variables: CompanyStakeholderDto | HumanStakeholderDto;
  }>();

  const { isOpen, handleOpenModal, handleSuccessModal, handleDismissModal, toggler } =
    useModalState({
      onSuccess: () => {
        if (!context) return;
        const { options, variables } = context;

        update(
          { companyId, stakeholderId, data: variables },
          {
            onSuccess: (data, variables, context) => {
              editStakeholderTracker();
              toast.success(messagesLocale['STAKEHOLDER_UPDATE_TOAST']);
              options?.onSuccess?.(data, variables.data, context);
              invalidateQuery();
              setClose();
            },
          },
        );
      },
    });
  if (stakeholderId && (isLoading || !stakeholder)) return <></>;

  return (
    <Sheet onOpenChange={(open) => !open && setClose()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={setClose}
        side="RIGHT"
      >
        <AlertDialogWrapper control={{ open: isOpen, onOpenChange: toggler }}>
          <UpdateStakeholderModal onDismiss={handleDismissModal} onSuccess={handleSuccessModal} />
        </AlertDialogWrapper>

        <div className="flex h-full w-full flex-col overflow-hidden overflow-y-auto rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-[550] text-gray-700">
              {isDefaultEditing ? (
                <AppFormattedMessage id="EDIT_STAKEHOLDER" />
              ) : (
                'View Stakeholder'
              )}
            </span>
            <CloseModalButton onClose={setClose} />
          </div>
          <EditStakeholderForm
            defaultData={stakeholder}
            isEditing={isDefaultEditing}
            mutateStakeholder={(variables, options) => {
              setContext({ variables, options });
              handleOpenModal();
            }}
            onEditClick={onEditClick}
            setCloseModal={setClose}
            stakeholderId={stakeholderId || ''}
            step={step}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default EditStakeholderModal;
